import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Lock, CreditCard, Loader2, Crown, Check, ShieldAlert } from 'lucide-react';
import { getBillingRole } from '../utils/billingRole';

const SaferpaySubscription = () => {
  const { API, user, language, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [accountMismatch, setAccountMismatch] = useState(null);
  const [checkoutEligible, setCheckoutEligible] = useState(true);

  const linkedRole = searchParams.get('billing_role');
  const initialRole = linkedRole === 'professional' ? 'professional' : getBillingRole(user);
  const [billingRole, setBillingRole] = useState(initialRole);
  const isClient = billingRole !== 'professional';
  const price = isClient ? 12 : 99;

  useEffect(() => {
    let active = true;
    axios.get(`${API}/saferpay/subscription-quote`, {
      params: linkedRole ? { billing_role: linkedRole } : undefined
    })
      .then(({ data }) => {
        if (active && ['client', 'professional'].includes(data?.role)) {
          setAccountMismatch(null);
          setBillingRole(data.role);
          setCheckoutEligible(data.eligible !== false);
        }
      })
      .catch((error) => {
        if (!active) return;
        const detail = error.response?.data?.detail;
        if (error.response?.status === 409 && detail?.code === 'subscription_account_mismatch') {
          setAccountMismatch(detail);
        }
        setBillingRole(initialRole);
      });
    return () => { active = false; };
  }, [API, initialRole]);

  const t = {
    en: {
      back: 'Back',
      title: 'Your Subscription',
      subtitle: isClient ? 'Client yearly subscription' : 'Professional yearly subscription',
      yearlyPlan: 'Yearly Plan',
      year: '/year',
      monthlyEquiv: isClient ? '= CHF 1.00/month' : '≈ CHF 8.25/month',
      whatsIncluded: "What's included:",
      subscribe: 'Subscribe Now',
      subscribeFor: 'Subscribe for',
      processing: 'Processing...',
      sandboxUnavailable: 'Preview checkout is waiting for the Saferpay test environment. No payment was started.',
      chargedNow: "You'll be charged CHF",
      now: 'now',
      securePayment: 'Secure payment powered by Worldline Saferpay',
      declineSubscription: 'Decline my subscription',
      havePromoCode: 'Have a promo code?',
      enterPromoCode: 'Enter promo code',
      applyCode: 'Apply',
      clientFeatures: [
        'Post unlimited jobs',
        'Chat with professionals',
        'Secure payments via Saferpay',
        'Access to verified providers',
        'Priority support'
      ],
      providerFeatures: [
        'Unlimited job applications',
        'Profile verification badge',
        'Priority in search results',
        'Direct messaging with clients',
        'Advanced analytics dashboard',
        'Priority support'
      ]
    },
    de: {
      back: 'Zurück',
      title: 'Ihr Abonnement',
      subtitle: isClient ? 'Kunden-Jahresabonnement' : 'Dienstleister-Jahresabonnement',
      yearlyPlan: 'Jahresplan',
      year: '/Jahr',
      monthlyEquiv: isClient ? '= CHF 1.00/Monat' : '≈ CHF 8.25/Monat',
      whatsIncluded: 'Was enthalten ist:',
      subscribe: 'Jetzt abonnieren',
      subscribeFor: 'Abonnieren für',
      processing: 'Wird verarbeitet...',
      sandboxUnavailable: 'Der Vorschau-Checkout wartet auf die Saferpay-Testumgebung. Es wurde keine Zahlung gestartet.',
      chargedNow: 'Ihnen werden CHF',
      now: 'berechnet',
      securePayment: 'Sichere Zahlung powered by Worldline Saferpay',
      declineSubscription: 'Mein Abonnement ablehnen',
      havePromoCode: 'Haben Sie einen Promo-Code?',
      enterPromoCode: 'Promo-Code eingeben',
      applyCode: 'Anwenden',
      clientFeatures: [
        'Unbegrenzt Jobs posten',
        'Mit Profis chatten',
        'Sichere Zahlungen via Saferpay',
        'Zugang zu verifizierten Anbietern',
        'Prioritäts-Support'
      ],
      providerFeatures: [
        'Unbegrenzte Jobbewerbungen',
        'Profilverifizierungs-Abzeichen',
        'Priorität in Suchergebnissen',
        'Direktnachrichten mit Kunden',
        'Erweiterte Analyse-Dashboard',
        'Prioritäts-Support'
      ]
    },
    it: {
      back: 'Indietro',
      title: 'Il tuo Abbonamento',
      subtitle: isClient ? 'Abbonamento annuale cliente' : 'Abbonamento annuale professionista',
      yearlyPlan: 'Piano Annuale',
      year: '/anno',
      monthlyEquiv: isClient ? '= CHF 1.00/mese' : '≈ CHF 8.25/mese',
      whatsIncluded: 'Cosa è incluso:',
      subscribe: 'Abbonati ora',
      subscribeFor: 'Abbonati per',
      processing: 'Elaborazione...',
      sandboxUnavailable: 'Il checkout di anteprima attende l’ambiente di test Saferpay. Nessun pagamento è stato avviato.',
      chargedNow: 'Ti verrà addebitato CHF',
      now: 'adesso',
      securePayment: 'Pagamento sicuro powered by Worldline Saferpay',
      declineSubscription: 'Rifiuta il mio abbonamento',
      havePromoCode: 'Hai un codice promo?',
      enterPromoCode: 'Inserisci codice promo',
      applyCode: 'Applica',
      clientFeatures: [
        'Pubblica lavori illimitati',
        'Chatta con professionisti',
        'Pagamenti sicuri via Saferpay',
        'Accesso a fornitori verificati',
        'Supporto prioritario'
      ],
      providerFeatures: [
        'Candidature illimitate',
        'Badge di verifica profilo',
        'Priorità nei risultati di ricerca',
        'Messaggistica diretta con i clienti',
        'Dashboard analisi avanzate',
        'Supporto prioritario'
      ]
    },
    fr: {
      back: 'Retour',
      title: 'Votre Abonnement',
      subtitle: isClient ? 'Abonnement annuel client' : 'Abonnement annuel professionnel',
      yearlyPlan: 'Plan Annuel',
      year: '/an',
      monthlyEquiv: isClient ? '= CHF 1.00/mois' : '≈ CHF 8.25/mois',
      whatsIncluded: 'Ce qui est inclus:',
      subscribe: "S'abonner maintenant",
      subscribeFor: "S'abonner pour",
      processing: 'Traitement...',
      sandboxUnavailable: 'Le paiement de prévisualisation attend l’environnement de test Saferpay. Aucun paiement n’a été lancé.',
      chargedNow: 'Vous serez facturé CHF',
      now: 'maintenant',
      securePayment: 'Paiement sécurisé powered by Worldline Saferpay',
      declineSubscription: 'Refuser mon abonnement',
      havePromoCode: 'Avez-vous un code promo?',
      enterPromoCode: 'Entrer le code promo',
      applyCode: 'Appliquer',
      clientFeatures: [
        'Publiez des emplois illimités',
        'Chattez avec des professionnels',
        'Paiements sécurisés via Saferpay',
        'Accès aux prestataires vérifiés',
        'Support prioritaire'
      ],
      providerFeatures: [
        'Candidatures illimitées',
        'Badge de profil vérifié',
        'Priorité dans les résultats de recherche',
        'Messagerie directe avec les clients',
        'Tableau de bord analytique avancé',
        'Support prioritaire'
      ]
    }
  };

  const text = t[language] || t.en;
  const features = isClient ? text.clientFeatures : text.providerFeatures;

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      localStorage.setItem('saferpay_return_page', '/saferpay-subscription');
      const response = await axios.post(
        `${API}/saferpay/subscription`,
        {},
        {
          params: {
            plan_type: 'yearly',
            ...(linkedRole ? { billing_role: linkedRole } : {})
          }
        }
      );
      if (response.data.redirect_url) {
        if (response.data.token) {
          localStorage.setItem('saferpay_token', response.data.token);
        }
        window.location.href = response.data.redirect_url;
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail?.code === 'payment_sandbox_required' ? text.sandboxUnavailable : detail?.message || detail || 'Failed to create payment session');
      setLoading(false);
    }
  };

  const handleSwitchAccount = async () => {
    await logout();
    navigate('/auth', { replace: true });
  };

  const handleSkip = async () => {
    try {
      await axios.post(`${API}/subscriptions/decline`);
      toast.warning('Subscription declined. Some features will be limited.');
    } catch (error) {
      console.error('Error declining subscription:', error);
    } finally {
      navigate(isClient ? '/dashboard/client' : '/dashboard/professional');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {text.back}
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Crown className="w-8 h-8 text-yellow-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="subscription-page-title">{text.title}</h1>
          <p className="text-gray-600" data-testid="subscription-plan-subtitle">{text.subtitle}</p>
        </div>

        {accountMismatch && (
          <div
            className="mb-6 border border-amber-300 bg-amber-50 p-4 text-amber-950"
            role="alert"
            data-testid="subscription-account-mismatch-alert"
          >
            <p className="font-semibold" data-testid="subscription-account-mismatch-title">
              Sign in with the professional account that received this email
            </p>
            <p className="mt-1 text-sm" data-testid="subscription-account-mismatch-message">
              You are currently signed in as {accountMismatch.signed_in_email || 'another Helply account'}.
            </p>
            <button
              type="button"
              onClick={handleSwitchAccount}
              className="mt-3 bg-amber-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-700 focus:ring-offset-2"
              data-testid="subscription-switch-account-button"
            >
              Switch account
            </button>
          </div>
        )}

        {/* Plan Card */}
        <div className="bg-white rounded-2xl shadow-lg border-2 border-blue-500 p-8 mb-6 relative" data-testid="yearly-plan-card">
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full">
            {text.yearlyPlan}
          </span>

          <div className="text-center mb-6 mt-2">
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-5xl font-bold text-blue-600" data-testid="subscription-plan-price">CHF {price % 1 === 0 ? price : price.toFixed(2)}</span>
              <span className="text-gray-500 text-lg">{text.year}</span>
            </div>
            <p className="text-green-600 font-medium mt-1">{text.monthlyEquiv}</p>
          </div>

          {/* Features */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">{text.whatsIncluded}</h3>
            <div className="space-y-2">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-green-600" />
                  </div>
                  <span className="text-gray-700 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Subscribe Button */}
          <button
            onClick={handleSubscribe}
            disabled={loading || Boolean(accountMismatch) || !checkoutEligible}
            data-testid="subscribe-btn"
            className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold py-4 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {text.processing}
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5" />
                {text.subscribeFor} CHF {price % 1 === 0 ? price : price.toFixed(2)}{text.year}
              </>
            )}
          </button>

          {/* Billing note */}
          <p className="text-center text-sm text-gray-500 mt-3" data-testid="subscription-charge-note">
            {text.chargedNow} {price % 1 === 0 ? price : price.toFixed(2)} {text.now}
          </p>
        </div>

        {!checkoutEligible && !isClient && (
          <div className="mb-6 flex gap-3 border border-amber-300 bg-amber-50 p-4 text-amber-950" role="alert" data-testid="subscription-verification-required-alert">
            <ShieldAlert className="h-5 w-5 shrink-0" />
            <div><p className="font-semibold">Manual verification required</p><p className="mt-1 text-sm">Your professional application must be approved before the CHF 99 yearly checkout becomes available.</p></div>
          </div>
        )}

        {/* Security Note */}
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-4">
          <Lock className="w-4 h-4" />
          <span>{text.securePayment}</span>
        </div>

        {/* Decline Option */}
        <div className="text-center">
          <button
            onClick={handleSkip}
            className="text-gray-500 hover:text-gray-700 text-sm underline"
            data-testid="decline-subscription-btn"
          >
            {text.declineSubscription}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaferpaySubscription;
